import * as React from "react";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {Popup} from "../Interfaces/interfaces";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import OdxTable, {TableHeaderCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Table";
import {OdxTableRow} from "../../../OdxComponents/NativeOdx/Table/Odx-TableRow";
import {OdxTableCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Tablecell";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import {odxIconSets} from "../../../OdxComponents/constants";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import Config_AddEditModal from "./Modals/Config_AddEdit";
import {getLinkedId} from "../../ProjectContent/Utils/projectInfo";
import {getConfigDataCall} from "../BackendFacade/apiCalls";
import {IAppConfig} from "../Interfaces/IConfig";
import {useModuleViewTracking} from "../../../Utils/sentryTracking";

const Config: React.FC = () => {
    const [popup, setPopup] = React.useState<Popup>(Popup.None);
    const [selectedKey, setSelectedKey] = React.useState<string>("");
    const [clientId, setClientId] = React.useState<string>("");

    const [appConfig, setAppConfig] = React.useState<IAppConfig>({});
    const [authLevelChanges, setAuthLevelChanges] = React.useState<string[]>([]);
    const [authLevels, setAuthLevels] = React.useState<string[]>([]);

    const {setLoading} = useMain();

    useModuleViewTracking('idms-config')

    React.useEffect(() => {
        setLoading(true);
        const prom1 = getConfigData();
        const prom2 = getLinkedIdIDMS();
        Promise.all([prom1, prom2]).then(() => {
            setLoading(false);
        });
    }, []);

    /**
     * Get the config data from the backend
     */
    const getConfigData = async () => {
        await getConfigDataCall(HubPathRouting.currentProject.id, HubPathRouting.currentModule.id).then((data) => {
            if (!data.success) {
                // HubFunctions.PopupCreateSingle("Error", data.message)
            } else {
                const appConfig = data.data.value;
                setAppConfig(appConfig);
                setAuthLevelChanges(createCombinationOfAllAuthLevels(appConfig.authLevels.split(",")));
                setAuthLevels(appConfig.authLevels.split(","));
            }
        })
    }

    /**
     * Create a list of all AuthLevelChangeuser-to combinations
     * @param authlevels
     */
    const createCombinationOfAllAuthLevels = (authlevels: string[]): string[] => {
        let authLevelsCombinations: string[] = ["AppDisplayName",
            "ClientID",
            "defaultAuth",
            "authLevels",
            "groupAuthLevels",
            "userTenant",
            "customDefaultAuthRules",
            "SignUpGroups",
            "SignUpGroups_OnPrem",
            "OverwriteWithGroups",
            "DisableTenantManagement",
            "SignUpWithExceptionAuth",
            "SignUpWithDefaultAuth",
            "SignUpEventAPI"]
        for (let i = 0; i < authlevels.length; i++) {
            for (let j = 0; j < authlevels.length; j++) {
                authLevelsCombinations.push("AuthLevelChange" + authlevels[i] + "To" + authlevels[j])
            }
            authLevelsCombinations.push("AuthLevelChange" + authlevels[i] + "ToAny")
            authLevelsCombinations.push("AuthLevelChangeAnyTo" + authlevels[i])
        }
        authLevelsCombinations.push("AuthLevelChangeAnyToAny")

        return authLevelsCombinations
    }

    const getLinkedIdIDMS = async () => {
        // Assume getLinkedId is a function that fetches the client ID
        // Replace this with your actual implementation
        const data = await getLinkedId(
            HubPathRouting.currentProject.id,
            HubPathRouting.currentModule.id
        );
        setClientId(data.projectId);
    };

    const openSidesheet = (key: string) => {
        setSelectedKey(key);
        setPopup(Popup.Config_AddEdit);
    };

    const closePopup = () => {
        setPopup(Popup.None);
        setSelectedKey("")
    };

    const getHeaderData = () => {
        const header: TableHeaderCell[] = [
            {name: 'key', title: 'Key', sortable: true},
            {name: 'value', title: 'Value', sortable: true},

            isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id, HubPathRouting.currentModule?.id) && {
                name: 'edit',
                title: 'Edit',
                required: true
            }
        ]
        return header;
    }

    const getTableContent = () => {
        const firstElement = <OdxTableRow key={-1}>
            <OdxTableCell>Client ID</OdxTableCell>
            <OdxTableCell>{clientId}</OdxTableCell>
            {isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id, HubPathRouting.currentModule?.id) && (
                <OdxTableCell className="text-right">
                    <odx-icon-button
                        variant="ghost"
                        icon={`${odxIconSets.core}::chevron-right`}
                        onClick={() => openSidesheet("ClientID")}
                    />
                </OdxTableCell>
            )}
        </OdxTableRow>
        const rest = Object.keys(appConfig).map((appkey, index) => (
            <OdxTableRow key={index}>
                <OdxTableCell>{appkey}</OdxTableCell>
                <OdxTableCell>{appConfig[appkey]}</OdxTableCell>
                {isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id, HubPathRouting.currentModule?.id) && (
                    <OdxTableCell className="text-center">
                        <odx-icon-button
                            variant="ghost"
                            icon={`${odxIconSets.core}::chevron-right`}
                            onClick={() => openSidesheet(appkey)}
                        />
                    </OdxTableCell>
                )}
            </OdxTableRow>
        ))
        return [firstElement, ...rest];
    }

    return (
        <div>
            <OdxBreadcrumbs/>
            {popup === Popup.Config_AddEdit && (
                <Config_AddEditModal
                    closeFunction={closePopup}
                    configKeys={Object.keys(appConfig)}
                    selectedKey={selectedKey}
                    clientId={clientId}
                    configData={appConfig}
                    refreshConfigData={getConfigData}
                    authLevelChanges={authLevelChanges}
                    isEdit={selectedKey != ""}
                />
            )}
            <OdxAreaHeader text="Configuration"
                           odxSize={'large'}
                           className={'!pl-0 !pr-0'}

                           odxContent={
                               isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id) && (
                                   <odx-button
                                       variant="accent"
                                       type="button"
                                       onClick={() => openSidesheet("")}
                                   >
                                       <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="md"></odx-icon>
                                       Add Configuration
                                   </odx-button>
                               )
                           }
            />


            <OdxTable
                headerData={getHeaderData()}
            >
                {getTableContent()}
            </OdxTable>
        </div>
    );
};

export default Config;
